import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, RendererFactory2, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';

import { ReachStorage } from '~ng-common-lib/core/utils/storage';

/**
 * This service shouldn't exists, but since apple sucks and doesn't support the Fullscreen API
 * we have to implement a workaround for it.
 *
 * This service is a temporary solution until we can remove it.
 */
@Injectable({ providedIn: 'root' })
export class FullscreenService {
	private readonly document = inject(DOCUMENT);
	private readonly renderer2Factory = inject(RendererFactory2);
	private readonly renderer2 = this.renderer2Factory.createRenderer(null, null);

	public readonly $fullscreenElement = signal<HTMLElement | null>(null);

	private fallbackParent: Node | null = null;
	private fallbackNextSibling: Node | null = null;
	private fallbackPrevStyles: {
		width?: string | null;
		height?: string | null;
		maxWidth?: string | null;
		maxHeight?: string | null;
		minWidth?: string | null;
		minHeight?: string | null;
	} | null = null; // styles so we can restore them later
	private readonly wrapperElement = this.createWrapper();
	private openedWithFallback = false;
	private lastDestroyRefFn: (() => void) | null = null;

	constructor() {
		this.renderer2.listen(this.document, 'fullscreenchange', () => {
			this.$fullscreenElement.set(this.document.fullscreenElement as HTMLElement | null);
		});

		this.renderer2.listen(this.document, 'keydown', (event: KeyboardEvent) => {
			if (event.key === 'Escape' && this.$fullscreenElement()) {
				this.exitFullscreen();
			}
		});
	}

	public async toggle(element: HTMLElement, destroyRef: DestroyRef | null): Promise<void> {
		if (this.$fullscreenElement() === element) {
			await this.exitFullscreen();
		} else {
			await this.requestFullscreen(element, destroyRef);
		}
	}

	/**
	 * To automatically exit the fullscreen mode when the element is destroyed,
	 * you can use the `destroyRef` parameter.
	 *
	 * (It can be injected in a component using `inject(DestroyRef)`).
	 */
	public async requestFullscreen(
		element: HTMLElement,
		destroyRef: DestroyRef | null
	): Promise<void> {
		try {
			await this.exitFullscreen();

			this.lastDestroyRefFn =
				destroyRef?.onDestroy(() => {
					if (this.$fullscreenElement() === element) {
						this.exitFullscreen();
					}
				}) ?? null;

			if (Capacitor.isNativePlatform()) {
				throw new Error('Native fullscreen is not supported on Capacitor platforms.');
			}

			await this.openNativeFullscreen(element);
		} catch (error) {
			this.openFallbackFullscreen(element);
		}
	}

	public async exitFullscreen(): Promise<void> {
		if (this.$fullscreenElement()) {
			this.lastDestroyRefFn?.(); // Unregister the destroyRef callback

			if (this.openedWithFallback) {
				await this.exitFallbackFullscreen();
			} else {
				await this.exitNativeFullscreen();
			}
		}
	}

	private async openNativeFullscreen(element: HTMLElement): Promise<void> {
		if (ReachStorage.getItem('__debug-fullscreen-native-disabled') === true) {
			throw new Error('Native fullscreen is disabled for debugging purposes.');
		}

		await element.requestFullscreen();
		this.$fullscreenElement.set(element);
		this.openedWithFallback = false;
	}

	private openFallbackFullscreen(element: HTMLElement): void {
		this.openedWithFallback = true;
		this.$fullscreenElement.set(element);
		this.fallbackParent = element.parentNode;
		this.fallbackNextSibling = element.nextSibling;
		this.fallbackPrevStyles = {
			width: element.style.width,
			height: element.style.height,
			maxWidth: element.style.maxWidth,
			maxHeight: element.style.maxHeight,
			minWidth: element.style.minWidth,
			minHeight: element.style.minHeight,
		};
		this.renderer2.appendChild(this.wrapperElement, element);
		this.renderer2.removeClass(this.wrapperElement, 'dis-none');
		this.renderer2.setStyle(element, 'width', '100%');
		this.renderer2.setStyle(element, 'max-width', '100%');
		this.renderer2.setStyle(element, 'min-width', '100%');
		this.renderer2.setStyle(element, 'height', '100%');
		this.renderer2.setStyle(element, 'max-height', '100%');
		this.renderer2.setStyle(element, 'min-height', '100%');
	}

	private async exitNativeFullscreen(): Promise<void> {
		await this.document.exitFullscreen();
	}

	private async exitFallbackFullscreen(): Promise<void> {
		const element = this.$fullscreenElement();
		if (element) {
			this.renderer2.addClass(this.wrapperElement, 'dis-none');
			this.renderer2.insertBefore(
				this.fallbackParent,
				element,
				this.fallbackNextSibling,
				true
			);
			this.$fullscreenElement.set(null);
			this.fallbackParent = null;
			this.fallbackNextSibling = null;
			this.renderer2.setStyle(element, 'width', this.fallbackPrevStyles?.width || '');
			this.renderer2.setStyle(element, 'max-width', this.fallbackPrevStyles?.maxWidth || '');
			this.renderer2.setStyle(element, 'min-width', this.fallbackPrevStyles?.minWidth || '');
			this.renderer2.setStyle(element, 'height', this.fallbackPrevStyles?.height || '');
			this.renderer2.setStyle(
				element,
				'max-height',
				this.fallbackPrevStyles?.maxHeight || ''
			);
			this.renderer2.setStyle(
				element,
				'min-height',
				this.fallbackPrevStyles?.minHeight || ''
			);
			this.fallbackPrevStyles = null;
		}
	}

	private createWrapper(): HTMLElement {
		const div = this.renderer2.createElement('div');
		this.renderer2.addClass(div, 'pos-fixed');
		this.renderer2.addClass(div, 'pos-t-0');
		this.renderer2.addClass(div, 'pos-l-0');
		this.renderer2.addClass(div, 'w-100');
		this.renderer2.addClass(div, 'h-100');
		this.renderer2.addClass(div, 'p-safe');
		this.renderer2.addClass(div, 'dis-none');
		this.renderer2.setStyle(div, 'z-index', '99999');

		this.renderer2.appendChild(this.document.body, div);
		return div;
	}
}
